/**
 * Script Preview Modal Component
 *
 * Displays generated script/narration for user review before full generation.
 * Allows users to approve, edit (per-section with voice/format controls), or regenerate.
 */

import React, { useState, useCallback, useEffect } from 'react';

import { useTranslation } from '../hooks/useTranslation';
import { useScriptEditState } from '../hooks/useScriptEditState';
import { ScriptEditPanel } from './script-preview/ScriptEditPanel';
import type { ScriptPreviewResponse, ScriptSection, ScriptEditOverrides } from '../types';

/**
 * Props for ScriptPreviewModal component.
 */
interface ScriptPreviewModalProps {
  /** Whether modal is open */
  isOpen: boolean;
  /** Close modal callback */
  onClose: () => void;
  /** Script preview data */
  preview: ScriptPreviewResponse | null;
  /** Loading state for preview generation */
  isLoading: boolean;
  /** Error message from preview generation (e.g. backend 5xx, rate-limited LLM) */
  error?: string | null;
  /** Callback when user approves the script (with optional config overrides) */
  onApprove: (script: string, overrides?: ScriptEditOverrides) => void;
  /** Callback when user wants to regenerate */
  onRegenerate: () => void;
  /** Generation type for display purposes */
  generationType: 'video' | 'audio';
  /** Current video config values for edit mode defaults */
  currentConfig?: {
    resolution?: string;
    tts_provider?: string;
    voice_id?: string;
    language?: string;
  };
}

/**
 * Format duration in seconds to a human-readable string
 */
function formatDuration(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);

  if (minutes === 0) {
    return `${remainingSeconds}s`;
  }

  return `${minutes}m ${remainingSeconds}s`;
}

/**
 * Script Section Display Component (read-only view)
 */
const SectionDisplay: React.FC<{ section: ScriptSection; index: number }> = ({
  section,
  index,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <div className="border border-gray-200 dark:border-secondary-600 rounded-lg mb-3 overflow-hidden">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 dark:bg-secondary-700/50 hover:bg-gray-100 dark:hover:bg-secondary-700 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-gray-500 dark:text-secondary-400">
            #{index + 1}
          </span>
          <span className="font-medium text-gray-900 dark:text-secondary-200">{section.title}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500 dark:text-secondary-400">
            {formatDuration(section.duration_seconds)}
          </span>
          <svg
            className={`w-5 h-5 text-gray-400 dark:text-secondary-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>
      {isExpanded && (
        <div className="px-4 py-3 bg-white dark:bg-secondary-800">
          <p className="text-gray-700 dark:text-secondary-300 whitespace-pre-wrap">
            {section.narration_text}
          </p>
        </div>
      )}
    </div>
  );
};

/**
 * Script Preview Modal component.
 */
export const ScriptPreviewModal: React.FC<ScriptPreviewModalProps> = ({
  isOpen,
  onClose,
  preview,
  isLoading,
  error,
  onApprove,
  onRegenerate,
  generationType,
  currentConfig,
}) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);

  const editState = useScriptEditState();

  // Reset editing state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setIsEditing(false);
      editState.reset();
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApprove = useCallback(() => {
    if (isEditing) {
      const narration = editState.buildFullNarration();
      const overrides = editState.buildOverrides();
      onApprove(narration, overrides);
    } else {
      onApprove(preview?.script?.full_narration || '');
    }
  }, [isEditing, editState, preview, onApprove]);

  const handleStartEditing = useCallback(() => {
    setIsEditing(true);
    // Initialize edit state from preview data + current config
    const sections = preview?.script?.sections || [];
    editState.initFromPreview(
      sections,
      currentConfig?.resolution,
      currentConfig?.tts_provider,
      currentConfig?.voice_id
    );
  }, [preview, currentConfig, editState]);

  const handleCancelEditing = useCallback(() => {
    setIsEditing(false);
    editState.reset();
  }, [editState]);

  if (!isOpen) return null;

  const script = preview?.script;
  const metadata = preview?.metadata;
  const language = currentConfig?.language || metadata?.language || 'en-us';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-2 xs:p-4">
      <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-xl w-full max-w-[calc(100vw-1rem)] xs:max-w-lg sm:max-w-2xl lg:max-w-4xl max-h-[calc(100vh-1rem)] xs:max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-4 xs:px-6 py-3 xs:py-4 border-b border-gray-200 dark:border-secondary-700 flex items-start xs:items-center justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg xs:text-xl font-bold text-gray-900 dark:text-white">
              {isEditing
                ? t('scriptPreview.editScript', { defaultValue: 'Edit Script' })
                : t('scriptPreview.title', { defaultValue: 'Script Preview' })}
            </h2>
            <p className="text-xs xs:text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2 xs:line-clamp-none">
              {isEditing
                ? t('scriptPreview.editHint', {
                    defaultValue:
                      'Edit the script above. Your changes will be used for generation.',
                  })
                : generationType === 'video'
                  ? t('scriptPreview.videoDescription', {
                      defaultValue: 'Review the generated video script before proceeding',
                    })
                  : t('scriptPreview.audioDescription', {
                      defaultValue: 'Review the generated narration before proceeding',
                    })}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1.5 xs:p-2 flex-shrink-0"
            type="button"
            aria-label="Close"
          >
            <svg
              className="w-5 h-5 xs:w-6 xs:h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-3 xs:px-6 py-3 xs:py-4">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-8 xs:py-12">
              <div className="animate-spin rounded-full h-10 w-10 xs:h-12 xs:w-12 border-b-2 border-blue-600 mb-3 xs:mb-4" />
              <p className="text-gray-600 dark:text-gray-300 text-sm xs:text-base text-center">
                {t('scriptPreview.generating', { defaultValue: 'Generating script...' })}
              </p>
              <p className="text-xs xs:text-sm text-gray-400 dark:text-gray-500 mt-2 text-center px-4">
                {t('scriptPreview.generatingHint', {
                  defaultValue:
                    'This can take from seconds to minutes depending on the duration of the video',
                })}
              </p>
            </div>
          ) : script ? (
            <div>
              {/* Stats Bar (only in read mode) */}
              {!isEditing && (
                <div className="flex flex-wrap gap-2 xs:gap-4 mb-4 xs:mb-6 p-3 xs:p-4 bg-gray-50 dark:bg-secondary-700 rounded-lg text-xs xs:text-sm">
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">
                      {t('scriptPreview.wordCount', { defaultValue: 'Words' })}:
                    </span>
                    <span className="ml-1 xs:ml-2 font-medium text-gray-900 dark:text-white">
                      {script.word_count}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">
                      {t('scriptPreview.estimatedDuration', { defaultValue: 'Est. Duration' })}:
                    </span>
                    <span className="ml-1 xs:ml-2 font-medium text-gray-900 dark:text-white">
                      {formatDuration(script.estimated_duration_seconds)}
                    </span>
                  </div>
                  {script.sections && script.sections.length > 0 && (
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">
                        {t('scriptPreview.sections', { defaultValue: 'Sections' })}:
                      </span>
                      <span className="ml-1 xs:ml-2 font-medium text-gray-900 dark:text-white">
                        {script.sections.length}
                      </span>
                    </div>
                  )}
                  {metadata && (
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">
                        {t('scriptPreview.generationTime', { defaultValue: 'Generated in' })}:
                      </span>
                      <span className="ml-1 xs:ml-2 font-medium text-gray-900 dark:text-white">
                        {metadata.generation_time_seconds}s
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Script Content */}
              {isEditing ? (
                <ScriptEditPanel
                  sections={editState.sections}
                  voiceConfig={{
                    tts_provider:
                      editState.voiceOverrides.tts_provider ||
                      currentConfig?.tts_provider ||
                      'kokoro',
                    voice_id: editState.voiceOverrides.voice_id || currentConfig?.voice_id || '',
                  }}
                  resolution={
                    editState.resolutionOverride || currentConfig?.resolution || '1920x1080'
                  }
                  language={language}
                  onSectionsChange={editState.updateSection}
                  onSectionDelete={editState.deleteSection}
                  onSectionMove={editState.moveSection}
                  onAddSection={editState.addSection}
                  onVoiceProviderChange={(provider) =>
                    // Clear voice_id when provider changes; VoiceQuickPicker will set the new
                    // default via onVoiceChange. Without this, the prior provider's voice would
                    // leak (e.g. pyttsx3 robot voice persisting on Chatterbox Turbo).
                    editState.setVoiceOverrides((prev) => ({
                      ...prev,
                      tts_provider: provider,
                      voice_id: undefined,
                    }))
                  }
                  onVoiceChange={(voice) =>
                    editState.setVoiceOverrides((prev) => ({
                      ...prev,
                      voice_id: voice,
                    }))
                  }
                  onResolutionChange={editState.setResolutionOverride}
                />
              ) : (
                <div>
                  {/* Sections View (for video) */}
                  {script.sections && script.sections.length > 0 ? (
                    <div>
                      {script.sections.map((section, index) => (
                        <SectionDisplay
                          key={`section-${index}-${section.title}`}
                          section={section}
                          index={index}
                        />
                      ))}
                    </div>
                  ) : (
                    /* Full Narration View (for audio) */
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-secondary-200 mb-4">
                        {t('scriptPreview.narration', { defaultValue: 'Narration' })}
                      </h3>
                      <div className="p-4 bg-gray-50 dark:bg-secondary-700 rounded-lg">
                        <p className="text-gray-700 dark:text-secondary-300 whitespace-pre-wrap">
                          {script.full_narration}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-8 xs:py-12 px-2">
              <div
                className="w-full max-w-xl border border-red-500/30 rounded-lg bg-red-900/20 p-4 xs:p-5"
                data-testid="script-preview-error"
              >
                <div className="flex items-start gap-3">
                  <svg
                    className="w-5 h-5 xs:w-6 xs:h-6 text-red-400 mt-0.5 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                    />
                  </svg>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm xs:text-base font-semibold text-red-200">
                      {t('scriptPreview.errorTitle', {
                        defaultValue: 'Failed to generate preview',
                      })}
                    </h3>
                    <p className="text-xs xs:text-sm text-red-300 mt-1 whitespace-pre-wrap break-words">
                      {error}
                    </p>
                    <p className="text-xs text-red-300/70 mt-2">
                      {t('scriptPreview.errorHint', {
                        defaultValue:
                          'Try again, switch the LLM model, or wait if the provider is rate-limited.',
                      })}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="empty-state-centered">
              <p className="text-gray-600 dark:text-secondary-400">
                {t('scriptPreview.noPreview', { defaultValue: 'No preview available' })}
              </p>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="px-3 xs:px-6 py-3 xs:py-4 border-t border-gray-200 dark:border-secondary-700 flex flex-col xs:flex-row flex-wrap gap-2 xs:gap-3 justify-between">
          <div className="flex flex-wrap gap-2 xs:gap-3 order-2 xs:order-1">
            {/* Regenerate Button - Only show when there is a preview to regenerate */}
            {script && (
              <button
                onClick={onRegenerate}
                disabled={isLoading}
                className="flex-1 xs:flex-none px-3 xs:px-4 py-2.5 xs:py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-secondary-700 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-secondary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 text-sm"
                type="button"
              >
                <svg
                  className="w-4 h-4 xs:w-5 xs:h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                <span className="hidden xs:inline">
                  {t('scriptPreview.regenerate', { defaultValue: 'Regenerate' })}
                </span>
                <span className="xs:hidden">
                  {t('scriptPreview.regenerate', { defaultValue: 'Regenerate' })}
                </span>
              </button>
            )}

            {/* Edit Toggle Button */}
            {!isLoading && script && (
              <button
                onClick={isEditing ? handleCancelEditing : handleStartEditing}
                className="flex-1 xs:flex-none px-3 xs:px-4 py-2.5 xs:py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-secondary-700 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-secondary-600 transition-colors flex items-center justify-center gap-2 text-sm"
                type="button"
              >
                {isEditing ? (
                  <>
                    <svg
                      className="w-4 h-4 xs:w-5 xs:h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                    <span className="hidden xs:inline">
                      {t('scriptPreview.cancelEdit', { defaultValue: 'Cancel Edit' })}
                    </span>
                    <span className="xs:hidden">
                      {t('common.cancel', { defaultValue: 'Cancel' })}
                    </span>
                  </>
                ) : (
                  <>
                    <svg
                      className="w-4 h-4 xs:w-5 xs:h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                    {t('scriptPreview.edit', { defaultValue: 'Edit' })}
                  </>
                )}
              </button>
            )}
          </div>

          <div className="flex gap-2 xs:gap-3 order-1 xs:order-2 w-full xs:w-auto">
            {/* Cancel Button */}
            <button
              onClick={onClose}
              className="flex-1 xs:flex-none px-3 xs:px-4 py-2.5 xs:py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-secondary-600 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-secondary-700 transition-colors text-sm"
              type="button"
            >
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </button>

            {/* Approve Button */}
            <button
              onClick={handleApprove}
              disabled={isLoading || !script}
              className="flex-1 xs:flex-none px-4 xs:px-6 py-2.5 xs:py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 text-sm"
              type="button"
            >
              <svg
                className="w-4 h-4 xs:w-5 xs:h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <span className="hidden sm:inline">
                {isEditing
                  ? t('scriptPreview.approveEdited', { defaultValue: 'Use Edited Script' })
                  : t('scriptPreview.approve', { defaultValue: 'Approve & Generate' })}
              </span>
              <span className="sm:hidden">
                {isEditing
                  ? t('common.use', { defaultValue: 'Use' })
                  : t('common.approve', { defaultValue: 'Approve' })}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScriptPreviewModal;
